import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, ShieldX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardFooter } from '../components/ui/card';
import { createClient } from '@supabase/supabase-js';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [supabase, setSupabase] = useState(null);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if URL contains valid reset token
  const checkForToken = () => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    // Check for any valid token format from Supabase
    const accessToken = params.get('access_token') || hashParams.get('access_token');
    const refreshToken = params.get('refresh_token') || hashParams.get('refresh_token');
    const code = params.get('code');
    const type = params.get('type') || hashParams.get('type');
    const tokenHash = params.get('token_hash') || hashParams.get('token_hash') || params.get('token') || hashParams.get('token');

    // Valid if we have access+refresh tokens, or code, or recovery type with token
    return !!(
      (accessToken && refreshToken) ||
      code ||
      (type === 'recovery' && tokenHash)
    );
  };

  useEffect(() => {
    // First check if there's a valid token in URL
    const tokenExists = checkForToken();
    setHasValidToken(tokenExists);

    if (!tokenExists) {
      setIsChecking(false);
      return;
    }

    // Initialize Supabase client only if token exists
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      setError('Supabase configuration is missing. Please contact support.');
      setIsChecking(false);
      return;
    }

    const client = createClient(supabaseUrl, supabaseAnonKey);
    setSupabase(client);

    // Handle the password reset token
    handleAuthCallback(client);
  }, []);

  const handleAuthCallback = async (client) => {
    try {
      // Parse tokens from both query params and URL hash
      const params = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      // Check for access_token + refresh_token (direct session)
      const accessToken = params.get('access_token') || hashParams.get('access_token');
      const refreshToken = params.get('refresh_token') || hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        await client.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        setIsChecking(false);
        return;
      }

      // Check for code (exchangeCodeForSession)
      const code = params.get('code');
      if (code) {
        await client.auth.exchangeCodeForSession(code);
        setIsChecking(false);
        return;
      }

      // Check for type=recovery + token_hash (verifyOtp)
      const type = params.get('type') || hashParams.get('type');
      const tokenHash = params.get('token_hash') || hashParams.get('token_hash') || params.get('token') || hashParams.get('token');

      if (type === 'recovery' && tokenHash) {
        await client.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
        setIsChecking(false);
        return;
      }

      // If no valid tokens found
      setHasValidToken(false);
      setIsChecking(false);
    } catch (err) {
      console.error('Auth callback error:', err);
      setError('Failed to verify reset link. The link may have expired. Please request a new password reset from the app.');
      setIsChecking(false);
    }
  };

  const validatePassword = (pass) => {
    if (pass.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pass)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pass)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pass)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!supabase) {
      setError('Supabase client not initialized');
      return;
    }

    setLoading(true);

    try {
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      // Sign out the user
      await supabase.auth.signOut();

      setSuccess(true);

      // Redirect to app after 3 seconds (uses configured URL or stays on current domain)
      setTimeout(() => {
        const redirectUrl = process.env.REACT_APP_REDIRECT_URL;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
        // If no redirect URL configured, user stays on success page
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking token
  if (isChecking) {
    return (
      <>
        <link rel="canonical" href="https://www.lingu-pix.app/reset-password" />
          <meta name="robots" content="noindex, nofollow" />
        <div className="min-h-screen bg-gradient-to-br from-[#FFF3E0] to-[#FFE5CC] flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-none shadow-2xl">
            <CardContent className="pt-12 pb-12 text-center space-y-6">
              <Loader2 size={48} className="text-[#FF7F30] animate-spin mx-auto" />
              <p className="text-[#666666]">Verifying reset link...</p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // No valid token - access denied
  if (!hasValidToken) {
    return (
      <>
        <link rel="canonical" href="https://www.lingu-pix.app/reset-password" />
          <meta name="robots" content="noindex, nofollow" />
        <div className="min-h-screen bg-gradient-to-br from-[#FFF3E0] to-[#FFE5CC] flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-none shadow-2xl">
            <CardContent className="pt-12 pb-12 text-center space-y-6">
              <div className="w-20 h-20 bg-[#FF4444]/10 rounded-full flex items-center justify-center mx-auto">
                <ShieldX size={48} className="text-[#FF4444]" />
              </div>
              <h2 className="text-2xl font-bold text-[#333333]">Access Denied</h2>
              <p className="text-[#666666] leading-relaxed">
                This page can only be accessed through a valid password reset link from the LinguPix app.
              </p>
              <p className="text-sm text-[#999999]">
                To reset your password, open the LinguPix app and use the "Forgot Password" option.
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <link rel="canonical" href="https://www.lingu-pix.app/reset-password" />
          <meta name="robots" content="noindex, nofollow" />
        <div className="min-h-screen bg-gradient-to-br from-[#FFF3E0] to-[#FFE5CC] flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-none shadow-2xl">
            <CardContent className="pt-12 pb-12 text-center space-y-6">
              <div className="w-20 h-20 bg-[#4CAF50] rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 size={48} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#333333]">Password Successfully Reset!</h2>
              <p className="text-[#666666] leading-relaxed text-lg">
                Your password has been updated successfully. You can now return to the LinguPix app and sign in with your new password.
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
        <link rel="canonical" href="https://www.lingu-pix.app/reset-password" />
        <meta name="robots" content="noindex, nofollow" />
      <div className="min-h-screen bg-gradient-to-br from-[#FFF3E0] to-[#FFE5CC] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-none shadow-2xl">
          <CardHeader className="text-center space-y-4 pb-8">
            <h1 className="text-4xl font-bold text-[#FF7F30]">LinguPix</h1>
            <h2 className="text-2xl font-bold text-[#333333]">Reset Your Password</h2>
            <p className="text-[#666666]">
              Enter your new password below. Make sure it's strong and memorable.
            </p>
          </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="bg-[#FF4444]/10 border border-[#FF4444] rounded-xl p-4 flex items-start space-x-3">
                <AlertCircle size={20} className="text-[#FF4444] flex-shrink-0 mt-0.5" />
                <p className="text-[#FF4444] text-sm">{error}</p>
              </div>
            )}

            {/* New Password */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#333333]">
                New Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-12 h-12 border-gray-300 focus:border-[#FF7F30] focus:ring-[#FF7F30]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#666666] hover:text-[#FF7F30] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#333333]">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pr-12 h-12 border-gray-300 focus:border-[#FF7F30] focus:ring-[#FF7F30]"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#666666] hover:text-[#FF7F30] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-[#F5F5F5] rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-[#333333]">Password must contain:</p>
              <ul className="text-sm text-[#666666] space-y-1">
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-[#FF7F30] rounded-full mr-2"></span>
                  At least 8 characters
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-[#FF7F30] rounded-full mr-2"></span>
                  One uppercase letter
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-[#FF7F30] rounded-full mr-2"></span>
                  One lowercase letter
                </li>
                <li className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-[#FF7F30] rounded-full mr-2"></span>
                  One number
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full bg-[#FF7F30] hover:bg-[#FFA55A] text-white font-semibold h-12 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="justify-center pb-8">
          <p className="text-sm text-[#666666]">
            Remember your password?{' '}
            <a href={process.env.REACT_APP_REDIRECT_URL || '/'} className="text-[#FF7F30] hover:underline font-semibold">
              Back to App
            </a>
          </p>
        </CardFooter>
      </Card>
    </div>
    </>
  );
};

export default ResetPassword;